import React from 'react';

import {useUnit} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {EMPTY_VALUE as EMPTY} from '@consts/common';
import {auditViolationModel} from '@entities/audit-violation/model';
import {ViolationHistoryDto} from '@entities/audit-violation/types';
import {Select, SelectOption} from '@shared/ui/Select';
import {Drawer} from '@shared/ui/Sidebar';
import {mapTableColumns, Table} from '@shared/ui/Table2.0';
import {Title} from '@shared/ui/Title';

import {
    formatViolationDateTime,
    formatViolationMoney,
    formatViolationTaxPeriod,
    localizeViolationTitle,
} from './ViolationGeneralInformation';
import {
    InfoLabel,
    InfoList,
    InfoValue,
    SectionCard,
    SectionGrid,
} from './ViolationGeneralInformation/styled';
import {Wrapper} from './ViolationsTab/styled';

const getColumnsConfig = (
    t: (key: string) => string,
    eventTypeOptions: Array<SelectOption<string>>,
) => [
    {id: 'jobId', header: t('page_audit.terms.id-job'), minWidth: 260},
    {id: 'validFrom', header: t('page_audit.terms.datetime'), minWidth: 151, isSortable: true},
    {
        id: 'eventType',
        header: t('page_audit.terms.event-type'),
        minWidth: 237,
        filter: <Select isClearable options={eventTypeOptions} />,
    },
    {id: 'declVatSum', header: t('page_audit.terms.vat-sum-total-by-nd'), minWidth: 180},
    {id: 'invVatSum', header: t('page_audit.terms.vat-sum-total-by-sf'), minWidth: 180},
    {id: 'gapSum', header: t('page_audit.terms.tax-gap'), minWidth: 160},
    {id: 'paySum', header: t('page_audit.terms.payment-amount'), minWidth: 160},
];

const InfoRow = ({label, value}: {label: string; value: React.ReactNode}) => (
    <>
        <InfoLabel>{label}</InfoLabel>
        <InfoValue>{value}</InfoValue>
    </>
);

type ViolationHistoryTableRow = ViolationHistoryDto & {
    __raw: ViolationHistoryDto;
};

export const ViolationHistoryTab = () => {
    const {t, i18n} = useTranslation();
    const model = useUnit(auditViolationModel);

    const eventTypeOptions = React.useMemo(
        () =>
            model.state.eventTypes.map((item) => ({
                label: localizeViolationTitle(item.title, i18n.language),
                value: item.code || item.id,
            })),
        [i18n.language, model.state.eventTypes],
    );

    const data = React.useMemo(
        () =>
            (model.state.history?.data ?? []).map((row: ViolationHistoryDto) => ({
                ...row,
                __raw: row,
                jobId: row.execution?.id || EMPTY,
                validFrom: formatViolationDateTime(row.validFrom),
                eventType: localizeViolationTitle(row.eventType?.title, i18n.language),
                declVatSum: formatViolationMoney(row.declVatSum),
                invVatSum: formatViolationMoney(row.invVatSum),
                gapSum: formatViolationMoney(row.gapSum),
                paySum: formatViolationMoney(row.paySum),
            })),
        [i18n.language, model.state.history],
    );

    const columns = React.useMemo(
        () =>
            mapTableColumns(
                getColumnsConfig(t, eventTypeOptions),
                {
                    eventType: model.state.historyEventType
                        ? eventTypeOptions.find(
                              (item) => item.value === model.state.historyEventType,
                          )
                        : undefined,
                },
                (filters) => {
                    const value = filters[0]?.value as SelectOption<string> | null | undefined;
                    model.historyEventTypeChanged(value?.value ?? null);
                },
                () => model.historyEventTypeChanged(null),
                {},
                () => undefined,
            ),
        [eventTypeOptions, model, t],
    );

    const selected = model.state.selectedHistoryItem;
    const selectedType = model.state.violationTypes.find((item) => item.id === selected?.type);

    return (
        <Wrapper>
            <Table
                columns={columns}
                data={data}
                isLoading={model.state.historyPending}
                pageCount={Math.ceil(
                    (model.state.history?.total ?? 0) / model.state.historyPageSize,
                )}
                pageIndex={model.state.historyPageNumber}
                totalRowsCount={model.state.history?.total ?? 0}
                onPageChange={model.historyPageNumberChanged}
                onPageSizeChange={model.historyPageSizeChanged}
                onRowClick={(row) => {
                    const payload = (row.original ?? row) as ViolationHistoryTableRow;
                    model.historyDetailsOpened(payload.__raw ?? payload);
                }}
            />

            <Drawer open={Boolean(selected)} onClose={model.historyDetailsClosed}>
                <Title size={4}>{localizeViolationTitle(selectedType?.title, i18n.language)}</Title>
                <SectionGrid style={{marginTop: 16}}>
                    <SectionCard>
                        <InfoList>
                            <InfoRow
                                label={t('page_audit.terms.violation-type')}
                                value={localizeViolationTitle(selectedType?.title, i18n.language)}
                            />
                            <InfoRow
                                label={t('page_audit.terms.datetime')}
                                value={formatViolationDateTime(selected?.validFrom)}
                            />
                            <InfoRow
                                label={t('page_audit.terms.event-type')}
                                value={localizeViolationTitle(
                                    selected?.eventType?.title,
                                    i18n.language,
                                )}
                            />
                            <InfoRow
                                label={t('page_audit.terms.tax-period')}
                                value={formatViolationTaxPeriod(selected?.taxPeriod)}
                            />
                        </InfoList>
                    </SectionCard>
                    <SectionCard>
                        <InfoList>
                            <InfoRow
                                label={t('page_audit.terms.vat-sum-total-by-nd')}
                                value={formatViolationMoney(selected?.declVatSum)}
                            />
                            <InfoRow
                                label={t('page_audit.terms.vat-sum-total-by-sf')}
                                value={formatViolationMoney(selected?.invVatSum)}
                            />
                            <InfoRow
                                label={t('page_audit.terms.tax-gap')}
                                value={formatViolationMoney(selected?.gapSum)}
                            />
                            <InfoRow
                                label={t('page_audit.terms.payment-amount')}
                                value={formatViolationMoney(selected?.paySum)}
                            />
                        </InfoList>
                    </SectionCard>
                </SectionGrid>
            </Drawer>
        </Wrapper>
    );
};
